import { MutableState } from 'src/replicant/State';
import { clearMatch2Timestamp } from 'src/replicant/match2/match2.modifiers';
import { getMatch2FrenzyIntroTimestampKey } from 'src/replicant/match2/match2.ruleset';
import { BlockId } from 'src/game/minigame/match2-timestep/game/defs/block';
import {
  getMatch2ActiveFrenzyId,
  getMatch2FrenzyEventConfig,
  getMatch2FrenzyMilestoneLevel,
  getMatch2FrenzyMilestoneReward,
  getMatch2FrenzyMultiplier,
  hasCompletedMatch2Frenzy,
  isMatch2FrenzyEnabled,
} from './match2-frenzy.getters';
import { Match2Reward } from '../rewards/match2-rewards.ruleset';

/**
 * Reset match2 frenzy progress and stats
 */
export function resetFrenzy(
  state: MutableState,
  now: number,
  forced = false,
): void {
  const { match2 } = state;
  const { frenzy } = match2;
  const activeFrenzyId = getMatch2ActiveFrenzyId(state, now);
  if (!activeFrenzyId || (!forced && frenzy.activeId === activeFrenzyId))
    return;
  frenzy.activeId = activeFrenzyId;
  frenzy.progress = 0;
  frenzy.unclaimedMilestone = [];
  clearMatch2Timestamp(state, getMatch2FrenzyIntroTimestampKey());
}

/**
 * Add to match2 frenzy progress
 */
export function progressFrenzy(
  state: MutableState,
  now: number,
  amount: number,
): number[] {
  if (
    !isMatch2FrenzyEnabled(state, now) ||
    hasCompletedMatch2Frenzy(state, now)
  )
    return [];
  const currentLevel = getMatch2FrenzyMilestoneLevel(state, now);
  state.match2.frenzy.progress += amount;
  const newLevel = getMatch2FrenzyMilestoneLevel(state, now);
  if (newLevel > currentLevel) {
    const levels = Array.from(
      {
        length:
          newLevel -
          currentLevel +
          (hasCompletedMatch2Frenzy(state, now) ? 1 : 0),
      },
      (_, i) => currentLevel + i,
    );
    state.match2.frenzy.unclaimedMilestone.push(...levels);
    return levels;
  }

  return [];
}

/**
 * Grant frenzy points.
 * @param state
 * @param now
 * @param blocksBroken
 * @param rewards
 */
export function grantFrenzyPoints(
  state: MutableState,
  now: number,
  blocksBroken: Partial<Record<BlockId, number>>,
  rewards: Match2Reward[],
): {
  eventID: string;
  level: number;
  rewardType: Match2Reward['type'] | undefined;
}[] {
  // Frenzy disabled, bail out.
  if (!isMatch2FrenzyEnabled(state, now)) return [];

  // Get current event config.
  const config = getMatch2FrenzyEventConfig(state, now);
  if (!config) return [];

  const objectives = config.objectives;
  const objectivesBroken = objectives.reduce((acc, key) => {
    const amount = blocksBroken[key] ?? 0;
    return acc + amount;
  }, 0);

  // No objectives broken, bail out.
  if (objectivesBroken === 0) return [];

  const multiplier = getMatch2FrenzyMultiplier(state, now);
  const finalObjectivesBroken = objectivesBroken * multiplier;

  // Level up as many times as possible.
  const levels = progressFrenzy(state, now, finalObjectivesBroken);
  const trackingEvents = levels.map((level) => {
    const reward = getMatch2FrenzyMilestoneReward(state, now, level);

    return {
      gameType: 'match2', // to distinguish from slots frenzy
      eventID: config.id,
      level,
      rewardType: reward?.type,
    } as const;
  });

  // Add reward to the set.
  rewards.push({
    type: 'feature',
    subType: 'frenzy_objective',
    amount: finalObjectivesBroken,
  });

  // Return tracking events.
  return trackingEvents;
}
